import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { resolveEntity } from '../../services/notification-center/notification-presentation';
import { resolveEntityTarget, prepareEntityNavigation } from '../../services/entity-link/entity-link';
import { NotificationRecord } from '../../models/notification-center.model';
import { NotificationCenterService } from '../../services/notification-center/notification-center.service';
import { authService } from '../../services/auth/auth.service';

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
// eslint-disable-next-line @angular-eslint/prefer-standalone -- declared in SharedModule alongside 250+ siblings; NgModule migration is a repo-wide ship, see docs/eslint-baseline.md
  standalone: false,
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  /** Guards the mark-all button while its requests are in flight. */
  markingAllRead = false;

  async onMarkAllRead(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    if (this.markingAllRead) return;
    this.markingAllRead = true;
    try {
      await this.notificationCenterService.markAllAsRead();
    } finally {
      this.markingAllRead = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private readonly notificationCenterService = inject(NotificationCenterService);
  private readonly authService = inject(authService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  @ViewChild('panelRoot', { static: false })
  panelRoot?: ElementRef<HTMLElement>;
  @ViewChild('panelContainer', { static: false }) panelContainer?: ElementRef<HTMLElement>;
  @ViewChild('filterAllButton', { static: false }) filterAllButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('triggerButton', { static: false }) triggerButton?: ElementRef<HTMLButtonElement>;

  get notifications$(): Observable<NotificationRecord[]> {
    return this.notificationCenterService.notifications$;
  }

  get unreadCount$(): Observable<number> {
    return this.notificationCenterService.unreadCount$;
  }

  get loading$(): Observable<boolean> {
    return this.notificationCenterService.loading$;
  }

  get error$(): Observable<string | null> {
    return this.notificationCenterService.errors$;
  }

  get selectedNotification$(): Observable<NotificationRecord | null> {
    return this.notificationCenterService.selectedNotification$;
  }

  get retentionDays$(): Observable<number | null> {
    return this.notificationCenterService.retentionDays$;
  }

  get filter$(): Observable<'all' | 'unread' | 'read'> {
    return this.notificationCenterService.filter$;
  }

  isPanelOpen = false;
  initialisationMessage: string | null = null;

  ngOnInit(): void {
    const userSub = this.authService.getCurrentUserSub();

    if (!userSub) {
      this.initialisationMessage =
        'Notifications are unavailable because your session is missing identity details. Please sign in again.';
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.notificationCenterService.initialize(userSub);
  }

  ngOnDestroy(): void {
    this.closePanelIfOpen();
  }

  togglePanel(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isPanelOpen) {
      this.isPanelOpen = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.notificationCenterService.refreshNotifications();
    this.notificationCenterService.refreshUnreadCount();
    this.isPanelOpen = true;
    queueMicrotask(() => this.focusFirstInteractiveElement());
    this.changeDetectorRef.markForCheck();
  }

  closePanel(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.closePanelIfOpen();
  }

  /** Mark one read in place, without opening the record it points at. */
  onMarkRead(notification: NotificationRecord): void {
    this.notificationCenterService.markAsRead(notification);
  }

  /** Remove one from the list. */
  onDismiss(notification: NotificationRecord): void {
    void this.notificationCenterService.dismiss(notification);
  }

  /** Clear one day's notifications — the Clear on a day heading. */
  onClearGroup(group: NotificationRecord[]): void {
    void this.notificationCenterService.dismissMany(group);
  }

  onNotificationClick(notification: NotificationRecord, event?: MouseEvent): void {
    event?.stopPropagation();

    // Selecting marks it read and expands it — keep that for rows that lead
    // nowhere, so a notification without a record is still dismissible.
    this.notificationCenterService.selectNotification(notification);

    // A notification you cannot act on is noise. Where the record is known,
    // open it. The route comes from the shared entity-link table rather than a
    // switch here, so a new notification type needs no change in this file.
    const entity = resolveEntity(notification);
    const target = resolveEntityTarget(entity);
    if (!target) return;

    prepareEntityNavigation(entity);
    this.closePanel();

    if (target.kind === 'external') {
      // Producer-supplied links may be signed downloads or point outside the
      // app; never push them through the router.
      window.open(target.href, '_blank', 'noopener,noreferrer');
      return;
    }
    void this.router.navigate(target.commands, { queryParams: target.queryParams });
  }

  onFilterChange(event: MouseEvent, filter: 'all' | 'unread' | 'read'): void {
    event.stopPropagation();
    this.notificationCenterService.setFilter(filter);
  }

  private closePanelIfOpen(): void {
    if (!this.isPanelOpen) {
      return;
    }
    this.isPanelOpen = false;
    queueMicrotask(() => this.returnFocusToTrigger());
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.isPanelOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    if (this.panelRoot?.nativeElement?.contains(target)) {
      return;
    }

    this.closePanelIfOpen();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closePanelIfOpen();
  }

  focusTrap(event: KeyboardEvent): void {
    if (!this.isPanelOpen || event.key !== 'Tab') {
      return;
    }

    const container = this.panelContainer?.nativeElement;
    if (!container) {
      return;
    }

    const focusableElements = Array.from(
      container.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

    if (!focusableElements.length) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  private focusFirstInteractiveElement(): void {
    const container = this.panelContainer?.nativeElement;
    if (!container) {
      return;
    }

    container.focus();

    const target =
      this.filterAllButton?.nativeElement ??
      container.querySelector<HTMLElement>('button, [href], [tabindex]:not([tabindex="-1"])');
    target?.focus();
  }

  private returnFocusToTrigger(): void {
    this.triggerButton?.nativeElement.focus();
  }
}
